import { useEffect, useState } from 'react';
import { parseTotp, generateCode, type ParsedTotp } from '@heybesafe/shared';
import { Icon } from './hbs/Icon';

interface Props {
  totp: string;                       // raw value from item.totp
  onCopied?: () => void;              // fired after a successful clipboard write
}

interface CodeState {
  code: string;        // raw digits, no spaces
  remainingMs: number;
  periodMs: number;
}

function formatCode(code: string): string {
  // Insert a single space at the midpoint for readability: "123456" -> "123 456",
  // "12345678" -> "1234 5678". Works for any even or odd length.
  const mid = Math.ceil(code.length / 2);
  return `${code.slice(0, mid)} ${code.slice(mid)}`;
}

export default function TotpCodeDisplay({ totp, onCopied }: Props): JSX.Element {
  const [parsed, setParsed] = useState<ParsedTotp | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [state, setState] = useState<CodeState | null>(null);

  // Parse on mount / when totp changes
  useEffect(() => {
    try {
      setParsed(parseTotp(totp));
      setParseError(null);
    } catch (e) {
      setParsed(null);
      setParseError(e instanceof Error ? e.message : 'Invalid TOTP secret');
    }
  }, [totp]);

  // Generate + refresh
  useEffect(() => {
    if (!parsed) return;
    let cancelled = false;

    async function refresh(): Promise<void> {
      const r = await generateCode(parsed!);
      if (!cancelled) {
        setState({ code: r.code, remainingMs: r.remainingMs, periodMs: parsed!.period * 1000 });
      }
    }

    refresh();
    // Tick every second to update the countdown; re-generate when period rolls.
    const interval = setInterval(refresh, 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [parsed]);

  if (parseError) {
    return (
      <div className="bg-hbs-surface border border-hbs-border rounded-hbs-md p-4 text-[14px] text-hbs-warning">
        Invalid TOTP secret
      </div>
    );
  }
  if (!state) {
    return (
      <div className="bg-hbs-surface border border-hbs-border rounded-hbs-md p-4 text-[14px] text-hbs-text-mute">
        Loading code…
      </div>
    );
  }

  const remainingSec = Math.ceil(state.remainingMs / 1000);
  const progress = state.remainingMs / state.periodMs; // 1 .. 0
  const circumference = 2 * Math.PI * 14; // r=14
  const dashOffset = circumference * (1 - progress);

  async function handleCopy(): Promise<void> {
    if (!state) return;
    await navigator.clipboard.writeText(state.code);
    onCopied?.();
  }

  return (
    <div className="bg-hbs-surface border border-hbs-border rounded-hbs-md p-4">
      <div className="text-[11.5px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute mb-2">
        Two-factor code
      </div>
      <div className="flex items-center gap-4">
        <span
          data-testid="totp-code"
          className="font-mono text-[28px] tracking-wider text-hbs-text tabular-nums"
        >
          {formatCode(state.code)}
        </span>
        <span
          className="relative inline-flex items-center justify-center"
          aria-label={`${remainingSec} seconds remaining`}
        >
          <svg width="32" height="32" viewBox="0 0 32 32" className="-rotate-90">
            <circle cx="16" cy="16" r="14" fill="none" stroke="currentColor" strokeWidth="2" className="text-hbs-border" />
            <circle
              cx="16" cy="16" r="14" fill="none"
              stroke="currentColor" strokeWidth="2"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              strokeLinecap="round"
              className="text-hbs-primary transition-[stroke-dashoffset] duration-1000 ease-linear"
            />
          </svg>
          <span className="absolute text-[11px] font-medium text-hbs-text-mute tabular-nums">
            {remainingSec}
          </span>
        </span>
        <button
          id="totp-copy-button"
          type="button"
          aria-label="Copy two-factor code"
          onClick={handleCopy}
          className="ml-auto p-1 rounded-hbs-sm text-hbs-text-mute hover:text-hbs-text hover:bg-hbs-surface-alt"
        >
          <Icon name="copy" size={16} />
        </button>
      </div>
    </div>
  );
}
